import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MultiSelect,
} from "@/components/ui";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Matter, MedicalTreatment } from "@/lib/types";
import { formatDate, trimText } from "@/lib/utils/format";
import { useRef, useState } from "react";
import { useNumberFormatter } from "react-aria";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";
interface SelectTreatmentBillsFormFieldProps {
  className?: string;
  emptyMessage?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  label?: string;
  matter: Matter;
  treatment: MedicalTreatment;
  onChange?: (value: string[]) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLDivElement>;
  register: UseFormRegisterReturn;
  registerNotApplicable?: UseFormRegisterReturn;
  disabled?: boolean;
}

export const SelectTreatmentBillsFormField = ({
  className,
  label,
  matter,
  treatment,
  onChange,
  placeholder,
  register,
  registerNotApplicable,
  disabled,
  isItemsLoading,
}: SelectTreatmentBillsFormFieldProps) => {
  const form = useFormContext();
  // This is needed to allow the select to be disabled when it is inside a disabled fieldset
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );
  const { openTreatmentBillModal } = useGlobalModal();
  const items = matter.medicalTreatmentBills.filter(
    (bill) => bill.medicalTreatmentId === treatment.id,
  );
  const formatter = useNumberFormatter({
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const NOT_APPLICABLE_VALUE = "__NOT_APPLICABLE__";

  const [selectedValues, setSelectedValues] = useState<string[]>(() => {
    const values =
      (form.getValues(register.name) as string[] | undefined) ?? [];
    const isNotApplicable = registerNotApplicable
      ? form.getValues(registerNotApplicable.name)
      : false;
    return isNotApplicable ? [NOT_APPLICABLE_VALUE] : values;
  });

  const addNewBill = () => {
    openTreatmentBillModal({
      matterId: matter.id,
      treatmentId: treatment.id,
      editing: false,
      initialTab: "details",
      afterCreateFn: (newBill) => {
        setSelectedValues([...selectedValues, newBill.data.id]);
        form.setValue(register.name, [...selectedValues, newBill.data.id], {
          shouldDirty: true,
        });
      },
      creatingFromAppointment: true,
    });
  };

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        return (
          <FormItem>
            {label && (
              <div className="flex">
                <FormLabel>{label}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
            )}
            <FormControl>
              <MultiSelect
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                addNewOption={{
                  label: "Add new bill",
                  onClick: addNewBill,
                }}
                isLoading={isItemsLoading}
                {...field}
                disabled={isInDisabledFieldset || disabled}
                onValueChange={(value) => {
                  const hasNotApplicable = value.includes(NOT_APPLICABLE_VALUE);

                  if (hasNotApplicable) {
                    // If "Not Applicable" is selected, only keep that value
                    const newValue = [NOT_APPLICABLE_VALUE];
                    setSelectedValues(newValue);
                    field.onChange([], { shouldDirty: true });
                    if (registerNotApplicable) {
                      form.setValue(registerNotApplicable.name, true, {
                        shouldDirty: true,
                      });
                    }
                  } else {
                    // If "Not Applicable" is not selected, handle normally
                    setSelectedValues(value);
                    field.onChange(value, { shouldDirty: true });
                    if (registerNotApplicable) {
                      form.setValue(registerNotApplicable.name, false, {
                        shouldDirty: true,
                      });
                    }
                  }
                  onChange?.(value);
                }}
                defaultValue={field.value}
                options={[
                  ...(registerNotApplicable
                    ? [
                        {
                          label: "Not applicable",
                          value: NOT_APPLICABLE_VALUE,
                          context: "Mark as not applicable",
                        },
                      ]
                    : []),
                  ...items.map((item) => {
                    const amountValue = Number(item.originalBill ?? 0);
                    const safeAmount = Number.isFinite(amountValue)
                      ? amountValue
                      : 0;
                    const formattedAmount = formatter.format(safeAmount);
                    const labelValue = trimText(item.recordLabel);
                    const displayLabel = labelValue
                      ? `${labelValue} (${formattedAmount})`
                      : formattedAmount;
                    const recordStatusContext =
                      item.recordStatus === "Received"
                        ? `Received on ${
                            item.recordReceivedDate
                              ? formatDate(item.recordReceivedDate)
                              : "<No Date>"
                          }`
                        : `Requested on ${formatDate(
                            item.recordRequestDate ?? "",
                          )}`;
                    const contextParts = [
                      recordStatusContext,
                      `Original Bill ${formattedAmount}`,
                    ];
                    return {
                      label: displayLabel,
                      value: item.id,
                      context: contextParts.filter(Boolean).join(" • "),
                    };
                  }),
                ]}
                modalPopover
                className={className}
                maxCount={1}
                placeholder={placeholder}
                ignoreAllSelected={true}
                showToggleAll={false}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};
